import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConsent } from '../../lib/consent-context';
import { useLegal } from '../../lib/legal-context';
import { ConsentToggle } from './ConsentToggle';

// Granular preference modal. Follows the existing .kyc-modal pattern (centered,
// blurred backdrop, full-screen on mobile). Opened from the banner's "Manage
// preferences" and from the footer's "Cookie preferences" link (withdrawal).
//
// Non-essential toggles default OFF (pre-ticking is unlawful under GDPR). The
// necessary category renders locked/always-on. Category copy from cmsLegal
// (REQ-008) with baseline fallbacks.

const FALLBACK = {
  necessary: 'Required for sign-in, security and core site features.',
  analytics: 'Helps us understand how the site is used so we can improve it.',
  marketing: 'Used to measure and personalise advertising.',
  functional: 'Remembers preferences such as language and layout.',
};

export function CookiePreferenceCenter() {
  const { prefsOpen, closePreferences, record, save, acceptAll, rejectAll } = useConsent();
  const legal = useLegal();
  const dialogRef = useRef<HTMLDivElement>(null);

  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [functional, setFunctional] = useState(false);

  useEffect(() => {
    if (!prefsOpen) return;
    // Seed from the stored record each time the modal opens.
    setAnalytics(record?.categories.analytics ?? false);
    setMarketing(record?.categories.marketing ?? false);
    setFunctional(record?.categories.functional ?? false);

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePreferences(); };
    document.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [prefsOpen, record, closePreferences]);

  if (!prefsOpen) return null;

  return (
    <div
      className="cookie-modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) closePreferences(); }}
    >
      <div
        className="cookie-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="cookie-modal-head">
          <h2 id="cookie-modal-title">Cookie preferences</h2>
          <button type="button" className="btn ghost sm" aria-label="Close cookie preferences" onClick={closePreferences}>✕</button>
        </div>
        <p className="cookie-modal-intro">
          Manage how we use cookies. Necessary cookies are always on. See our{' '}
          <Link to="/cookies" target="_blank" rel="noopener noreferrer">Cookie Policy</Link>.
        </p>

        <ConsentToggle
          id="cat-necessary" label="Strictly necessary" checked locked
          description={legal.cookie_cat_necessary ?? FALLBACK.necessary}
        />
        <ConsentToggle
          id="cat-analytics" label="Analytics" checked={analytics} onChange={setAnalytics}
          description={legal.cookie_cat_analytics ?? FALLBACK.analytics}
        />
        <ConsentToggle
          id="cat-marketing" label="Marketing" checked={marketing} onChange={setMarketing}
          description={legal.cookie_cat_marketing ?? FALLBACK.marketing}
        />
        <ConsentToggle
          id="cat-functional" label="Functional" checked={functional} onChange={setFunctional}
          description={legal.cookie_cat_functional ?? FALLBACK.functional}
        />

        <div className="cookie-modal-actions">
          <button
            type="button" className="btn accent"
            onClick={() => save({ necessary: true, analytics, marketing, functional }, 'custom')}
          >
            Save preferences
          </button>
          <button type="button" className="btn ghost" onClick={rejectAll}>Reject all</button>
          <button type="button" className="btn ghost" onClick={acceptAll}>Accept all</button>
        </div>
      </div>
    </div>
  );
}
